import React, { useState, useEffect, useRef } from 'react';
import { TelemetryPanelConfig } from '../types/telemetry-dashboard.types';
import { getTelemetrySubColor, getTelemetryVizLabel } from '../utils/telemetry-color.utils';
import {
  alertLevel,
  getSubsystemLabel,
  getChannelDef,
  makeColumns,
  tableColDef,
  unitOf,
  CHART_MAX_UNITS,
  colKey,
  getTelemetrySeriesColor,
  tableColLabel,
  hasPanelTelemetryData,
} from '../utils/telemetry-helpers.utils';
import { mkVizIcon, ico } from '../utils/telemetry-icons.utils';
import '../styles/telemetry-dashboard-panel.css';
import { useTelemetryLayoutContext } from './TelemetryLayout';
import CustomToolTip from '../../CustomToolTip';

/* =========================================================================
   0a. TelemetryCardTitle Component
   ========================================================================= */
export interface TelemetryCardTitleProps {
  title?: string;
  children?: React.ReactNode;
  subColor?: string;
  subtitle?: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
}

export const TelemetryCardTitle: React.FC<TelemetryCardTitleProps> = ({
  title,
  children,
  subColor,
  subtitle,
  className = '',
  style,
}) => {
  const tooltipText = title ?? (typeof children === 'string' ? children : '');

  return (
    <div className={`telemetry-panel-title-wrap ${className}`} style={style}>
      <CustomToolTip title={tooltipText} className="telemetry-panel-title-tooltip">
        <span className="telemetry-panel-title">{children}</span>
      </CustomToolTip>
      {subtitle && (
        <span className="telemetry-panel-subtitle">
          {subtitle}
        </span>
      )}
    </div>
  );
};

/* =========================================================================
   0b. TelemetryCardBody Component
   ========================================================================= */
export interface TelemetryCardBodyProps {
  children?: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
}

export const TelemetryCardBody: React.FC<TelemetryCardBodyProps> = ({ children, className = '', style }) => (
  <div className={`telemetry-card-body ${className}`} style={style}>
    {children}
  </div>
);

/* =========================================================================
   1. TelemetryCard Container Component
   ========================================================================= */
export interface TelemetryCardProps {
  panel: TelemetryPanelConfig | any;
  children?: React.ReactNode;
  isDrag?: boolean;
  isResize?: boolean;
  isSubsystemTab?: boolean;
  className?: string;
  style?: React.CSSProperties;
  onContextMenu?: (e: React.MouseEvent) => void;
  onResizeDown?: (dir: string, e: React.PointerEvent) => void;
}

export const TelemetryCard: React.FC<TelemetryCardProps> = ({
  panel,
  children,
  isDrag,
  isResize,
  className = '',
  style,
  onContextMenu,
  onResizeDown,
}) => {
  const { layoutMode } = useTelemetryLayoutContext();
  const dirs = layoutMode === 'list' ? ['s'] : ['n', 's', 'e', 'w', 'ne', 'nw', 'se', 'sw'];
  const lvl = alertLevel(panel as any);

  return (
    <div
      id={`telemetry-widget-card-${panel.id}`}
      className={`dashboard-card-wrap td-card-container ${lvl ? `td-card-${lvl}` : ''} ${className}`}
      style={style}
      onContextMenu={onContextMenu}
    >
      {/* Resize Handles */}
      {onResizeDown &&
        dirs.map((dir) => (
          <div
            key={dir}
            id={`telemetry-resize-handle-${dir}-${panel.id}`}
            className={`td-card-rz td-card-rz--${dir}`}
            onPointerDown={(e) => onResizeDown(dir, e)}
          />
        ))}

      {children}
    </div>
  );
};

/* =========================================================================
   2. TelemetryCardHeader Component
   ========================================================================= */
export interface TelemetryCardHeaderProps {
  panel?: TelemetryPanelConfig | any;
  children?: React.ReactNode;
  onDragDown?: (e: React.PointerEvent) => void;
  className?: string;
  style?: React.CSSProperties;
}

export const TelemetryCardHeader: React.FC<TelemetryCardHeaderProps> = ({
  panel,
  children,
  onDragDown,
  className = '',
  style,
}) => {
  const { layoutMode } = useTelemetryLayoutContext();
  return (
    <div
      id={panel ? `telemetry-widget-header-${panel.id}` : undefined}
      className={`telemetry-widget-header ${className}`}
      onPointerDown={onDragDown}
      style={{ cursor: (onDragDown && (layoutMode === 'grid' || layoutMode === 'list')) ? 'grab' : 'default', ...style }}
    >
      {children}
    </div>
  );
};

/* =========================================================================
   3. TelemetryCardSubsystemSelect Component
   ========================================================================= */
export interface TelemetryCardSubsystemSelectProps {
  panel: TelemetryPanelConfig | any;
  onSubClick?: (e: React.MouseEvent) => void;
  isSubsystemTab?: boolean;
  activeMenuTriggerId?: string | null;
}

export const TelemetryCardSubsystemSelect: React.FC<TelemetryCardSubsystemSelectProps> = ({
  panel,
  onSubClick,
  isSubsystemTab,
  activeMenuTriggerId,
}) => {
  const first = panel.parameters?.[0] || { sub: '', tel: '' };
  const subColor = getTelemetrySubColor(first.sub);
  const currentSubLabel = getSubsystemLabel(first.sub);
  const isSubOpen = activeMenuTriggerId === `telemetry-widget-sub-btn-${panel.id}`;

  if (isSubsystemTab) {
    return (
      <div className="subsystem-title-section">
        <span className="telemetry-panel-sub-dot" style={{ background: subColor }} />
        <span className="td-truncate">
          {currentSubLabel}
        </span>
      </div>
    );
  }

  return (
    <button
      id={`telemetry-widget-sub-btn-${panel.id}`}
      data-nodrag
      onClick={onSubClick}
      className="widget-sub-btn"
    >
      <span className="telemetry-panel-sub-dot" style={{ background: subColor }} />
      {currentSubLabel}
      <svg
        width="9"
        height="9"
        viewBox="0 0 16 16"
        fill="none"
        className={`td-chevron-icon ${isSubOpen ? 'td-chevron-icon--open' : ''}`}
      >
        <path d="M4 6l4 4 4-4" stroke="#7B7B7B" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </button>
  );
};

/* =========================================================================
   4. TelemetryCardParamSelect Component
   ========================================================================= */
export interface TelemetryCardParamSelectProps {
  panel: TelemetryPanelConfig | any;
  onTelClick?: (e: React.MouseEvent) => void;
  activeMenuTriggerId?: string | null;
}

export const TelemetryCardParamSelect: React.FC<TelemetryCardParamSelectProps> = ({
  panel,
  onTelClick,
  activeMenuTriggerId,
}) => {
  const first = panel.parameters?.[0] || { sub: '', tel: '' };
  const def = getChannelDef(first.sub, first.tel);
  const subColor = getTelemetrySubColor(first.sub);
  const currentTelLabel = panel.name || def.label || first.tel;
  const isTelOpen = activeMenuTriggerId === `telemetry-widget-tel-btn-${panel.id}`;

  if (panel.name || !onTelClick) {
    return (
      <div className="telemetry-panel-title-wrap">
        {/* <span className="telemetry-panel-sub-dot" style={{ background: subColor }} /> */}
        <span className="telemetry-panel-title">
          {panel.name || currentTelLabel}
        </span>
      </div>
    );
  }

  return (
    <button
      id={`telemetry-widget-tel-btn-${panel.id}`}
      data-nodrag
      onClick={onTelClick}
      className="widget-tel-btn"
    >
      <span className="td-truncate">
        {currentTelLabel}
      </span>
      <svg
        width="9"
        height="9"
        viewBox="0 0 16 16"
        fill="none"
        className={`td-chevron-icon ${isTelOpen ? 'td-chevron-icon--open' : ''}`}
      >
        <path d="M4 6l4 4 4-4" stroke="#7B7B7B" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </button>
  );
};



/* =========================================================================
   5. TelemetryCardAlertBadge Component
   ========================================================================= */
export interface TelemetryCardAlertBadgeProps {
  panel: TelemetryPanelConfig | any;
}

export const TelemetryCardAlertBadge: React.FC<TelemetryCardAlertBadgeProps> = ({ panel }) => {
  const lvl = alertLevel(panel as any);
  if (!lvl) return null;

  return (
    <span
      id={`telemetry-widget-alert-badge-${panel.id}`}
      className={`telemetry-alert-badge telemetry-alert-badge--${lvl}`}
    >
      {lvl === 'crit' ? 'CRIT' : 'WARN'}
    </span>
  );
};

/* =========================================================================
   6. TelemetryCardVizSelect Component
   ========================================================================= */
export interface TelemetryCardVizSelectProps {
  panel: TelemetryPanelConfig | any;
  onVizClick?: (e: React.MouseEvent) => void;
  activeMenuTriggerId?: string | null;
}

export const TelemetryCardVizSelect: React.FC<TelemetryCardVizSelectProps> = ({
  panel,
  onVizClick,
  activeMenuTriggerId,
}) => {
  const vizMode = panel.graphMode;
  const isVizOpen = activeMenuTriggerId === `telemetry-widget-viz-btn-${panel.id}`;

  if (vizMode === 'kpi' || panel?.parameters?.length > 65) return null;

  const tooltipTitle = 'Visualization type';

  return (
    <CustomToolTip title={tooltipTitle}>
      <button
        id={`telemetry-widget-viz-btn-${panel.id}`}
        data-nodrag
        onClick={onVizClick}
        aria-label={tooltipTitle}
        className={`viz-drop-button`}
      >
        {mkVizIcon(vizMode)}
        <span>{getTelemetryVizLabel(vizMode)}</span>
        <svg
          width="9"
          height="9"
          viewBox="0 0 16 16"
          fill="none"
          className={`td-chevron-icon ${isVizOpen ? 'td-chevron-icon--open' : ''}`}
        >
          <path d="M4 6l4 4 4-4" stroke="#7B7B7B" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
    </CustomToolTip>
  );
};

/* =========================================================================
   7. TelemetryCardActions Component
   ========================================================================= */
export interface TelemetryCardActionsProps {
  children?: React.ReactNode;
}

export const TelemetryCardActions: React.FC<TelemetryCardActionsProps> = ({ children }) => {
  return (
    <div className="telemetry-card-actions">
      {children}
    </div>
  );
};

/* =========================================================================
   8a. TelemetryCardSearchInput Component (RegEx & Time/Param Search)
   ========================================================================= */
export interface TelemetryCardSearchInputProps {
  panel: TelemetryPanelConfig | any;
  placeholder?: string;
}

export const TelemetryCardSearchInput: React.FC<TelemetryCardSearchInputProps> = ({
  panel,
  placeholder = 'Search or RegEx...',
}) => {
  const layoutCtx = useTelemetryLayoutContext();
  const patchWidget = layoutCtx?.patchWidget;
  const numParams = panel?.parameters?.length || 0;
  const hasData = hasPanelTelemetryData(panel);

  const [localQuery, setLocalQuery] = useState(panel?.searchQuery || '');
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Synchronize local state if panel.searchQuery changes externally
  useEffect(() => {
    setLocalQuery(panel?.searchQuery || '');
  }, [panel?.searchQuery]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  // Render for table panels (ctable / table) with > 65 parameters
  const isTableMode = panel?.graphMode === 'ctable' || panel?.graphMode === 'table';
  if (!isTableMode || numParams <= 65) {
    return null;
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!hasData) return;
    const val = e.target.value;
    setLocalQuery(val); // Instant 0ms local UI feedback

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      if (patchWidget) {
        patchWidget(panel.id, { searchQuery: val });
      }
    }, 150);
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    setLocalQuery('');
    if (patchWidget) {
      patchWidget(panel.id, { searchQuery: '' });
    }
  };

  return (
    <div
      className={`td-card-search-wrap ${!hasData ? 'td-card-search-wrap--disabled' : ''}`}
      data-nodrag
      onClick={(e) => e.stopPropagation()}
    >
      <svg className="td-card-search-icon" width="12" height="12" viewBox="0 0 16 16" fill="none">
        <circle cx="7" cy="7" r="4.5" stroke="currentColor" strokeWidth="1.4" />
        <path d="M10.5 10.5L14 14" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
      </svg>
      <input
        type="text"
        disabled={!hasData}
        value={hasData ? localQuery : ''}
        onChange={handleChange}
        placeholder={hasData ? placeholder : 'No data to search'}
        title={hasData ? 'Search parameter name, subsystem, or time (Supports RegEx e.g. volt|curr, 04:15)' : 'No telemetry data available'}
        className="td-card-search-input"
      />
      {hasData && localQuery && (
        <button
          type="button"
          onClick={handleClear}
          title="Clear search"
          className="td-card-search-clear"
        >
          ✕
        </button>
      )}
    </div>
  );
};

/* =========================================================================
   8. TelemetryCardExpandButton Component
   ========================================================================= */
export interface TelemetryCardExpandButtonProps {
  panel?: TelemetryPanelConfig | any;
  onExpand?: () => void;
}

export const TelemetryCardExpandButton: React.FC<TelemetryCardExpandButtonProps> = ({ panel, onExpand }) => {
  if (!onExpand || panel?.graphMode === 'kpi') return null;

  return (
    <CustomToolTip title="Expand View" placement='top'>
      <button
        id={panel?.id ? `telemetry-widget-expand-btn-${panel.id}` : undefined}
        data-nodrag
        onClick={onExpand}
        className="telemetry-card-hdr-btn"
      >
        {ico('expand', 14)}
      </button>
    </CustomToolTip>
  );
};

/* =========================================================================
   9. TelemetryCardMenuButton Component (3-Dots Action Menu)
   ========================================================================= */
export interface TelemetryCardMenuButtonProps {
  panel?: TelemetryPanelConfig | any;
  onClick?: (e: React.MouseEvent) => void;
}

export const TelemetryCardMenuButton: React.FC<TelemetryCardMenuButtonProps> = ({ panel, onClick }) => {
  if (!onClick) return null;

  return (
    <CustomToolTip title='Panel Menu' placement='top'>
      <button
        id={panel?.id ? `telemetry-widget-menu-btn-${panel.id}` : undefined}
        data-nodrag
        onClick={onClick}
        title="Panel Menu"
        className="telemetry-card-hdr-btn"
      >
        {ico('dots', 14)}
      </button>
    </CustomToolTip>
  );
};


/* =========================================================================
   10. TelemetryCardBottomLegend Component (Interactive Multi-Chart Cards)
   ========================================================================= */
export interface TelemetryCardBottomLegendProps {
  panel: TelemetryPanelConfig | any;
  units?: any;
  patchWidget?: (id: string, patch: Partial<any>) => void;
  ecInstances?: any;
}

export const TelemetryCardBottomLegend: React.FC<TelemetryCardBottomLegendProps> = ({
  panel,
  units = {},
  patchWidget = () => { },
  ecInstances,
}) => {
  if (panel.graphMode !== 'cchart') return null;

  const first = panel.parameters?.[0] || { sub: '', tel: '' };
  let rawCols = panel.parameters && panel.parameters.length > 0 ? panel.parameters : makeColumns(first.sub, first.tel);

  const cols: Array<{ sub: string; tel: string; uniqueId?: string; si: number | null; threshold?: any }> = [];
  rawCols.forEach((c: any) => {
    if (c.si == null) {
      const def = tableColDef(c);
      if (def && def.series && def.series.length > 0) {
        def.series.forEach((_: any, i: number) => {
          cols.push({ ...c, si: i });
        });
      } else {
        cols.push(c);
      }
    } else {
      cols.push(c);
    }
  });

  if (cols.length === 0) return null;

  const ulist: string[] = [];
  cols.forEach((c) => {
    const def = tableColDef(c);
    const u = unitOf(def, units) || def?.unit || '';
    if (u && !ulist.includes(u) && ulist.length < CHART_MAX_UNITS) ulist.push(u);
  });

  const hidden = new Set(panel.hiddenCols || []);

  const toggleCol = (key: string) => {
    const hiddenArr: string[] = panel.hiddenCols || [];
    const nextHidden = hiddenArr.includes(key)
      ? hiddenArr.filter((x) => x !== key)
      : [...hiddenArr, key];
    patchWidget(panel.id, { hiddenCols: nextHidden });
  };

  let visCount = -1;

  return (
    <div data-nodrag className="td-cchart-bottom-legend">
      {cols.map((c, i) => {
        const key = colKey(c);
        const isVisible = !hidden.has(key);
        if (isVisible) visCount++;
        const vIndex = isVisible ? visCount : -1;
        const color = getTelemetrySeriesColor(i);
        const label = tableColLabel(c);
        const def = tableColDef(c);
        const unit = unitOf(def, units) || def?.unit || '';

        let ui = ulist.indexOf(unit);
        if (ui < 0) ui = 0;
        const side = ui % 2 === 0 ? 'L' : 'R';
        const unitAxisText = unit ? `${unit} · ${side}` : side;

        const subLabel = (getSubsystemLabel(c.sub) || c.sub || 'GEN').toUpperCase();
        const subCol = getTelemetrySubColor(c.sub);

        return (
          <div
            key={key}
            onClick={(e) => {
              e.stopPropagation();
              toggleCol(key);
            }}
            onMouseEnter={() => {
              if (vIndex < 0 || !ecInstances?.current) return;
              const inst = ecInstances.current[panel.id];
              if (inst && inst.ec) {
                inst.ec.dispatchAction({ type: 'highlight', seriesIndex: vIndex });
              }
            }}
            onMouseLeave={() => {
              if (!ecInstances?.current) return;
              const inst = ecInstances.current[panel.id];
              if (inst && inst.ec) {
                inst.ec.dispatchAction({ type: 'downplay' });
              }
            }}
            title={isVisible ? 'Click to hide series' : 'Click to show series'}
            className={`td-legend-card ${!isVisible ? 'td-legend-card--inactive' : ''}`}
          >
            <span
              className="td-legend-card-dot"
              style={{
                background: isVisible ? color : 'transparent',
                border: isVisible ? `1px solid ${color}` : `1px solid ${color}66`,
              }}
            />

            <div className="td-legend-card-info">
              <div className="td-legend-card-header">
                <span className="td-legend-card-title">{label}</span>
                {unitAxisText && <span className="td-legend-card-unit">{unitAxisText}</span>}
              </div>
              <div className="td-legend-card-sub" style={{ color: subCol }}>
                {subLabel}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};
